import React, { useMemo, useState } from "react";
import { Accordion, AccordionSummary, Button, Stack, styled, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { Props as ParentProps, Option } from "./TableColumnsGroup";

const StyledAccordion = styled(Accordion)(() => ({
  "&.Mui-expanded": { margin: "0 !important" },
  ".MuiAccordionSummary-root": {
    minHeight: "32px"
  },
  ".MuiAccordionSummary-content": {
    margin: "8px 0"
  },
  "&.MuiAccordion-root:before": {
    background: "transparent"
  }
}));

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  fontSize: "16px",
  fontWeight: "500",
  "&.MuiAccordionSummary-root": { display: "flex", flexDirection: "row-reverse", gap: "12px" }
}));

interface Props extends Pick<ParentProps, "options" | "onSubmit" | "menuTitle"> {
  onClose: () => void;
  incomingState: Option[];
}

type RenderState = { groups: { key: string; title: string; options: Option[] }[]; ungroupedOptions: Option[] };

export const Content: React.FC<Props> = ({
  onClose,
  onSubmit,
  incomingState,
  options,
  menuTitle = "Manage columns"
}) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const [state, setState] = useState<Option[]>(incomingState);

  const handleChangeAccordion = (groupKey: string) => {
    if (expanded === groupKey) {
      return setExpanded(false);
    }
    setExpanded(groupKey);
  };

  const handleChangeColumn = (option: Option) => {
    if (state.some((o) => o.key === option.key && o.groupKey === option.groupKey)) {
      return setState(state.filter((o) => o.key !== option.key || o.groupKey !== option.groupKey));
    }
    setState([...state, option]);
  };

  const isColumnSelected = (option: Option) =>
    state.some((c) => c.key === option.key && c.groupKey === option.groupKey);

  const handleSubmit = () => {
    onSubmit(state);
    onClose();
  };

  const renderState = useMemo<RenderState>(() => {
    const result: RenderState = { groups: [], ungroupedOptions: [] };

    options.forEach((option) => {
      if (option.groupKey) {
        // if the options belongs to a group
        if (result.groups.some((g) => g.key === option.groupKey)) {
          // if such group exists
          result.groups.find((g) => g.key === option.groupKey)?.options.push(option); // add the option to the group
        } else {
          result.groups.push({ key: option.groupKey, title: option.groupKey, options: [option] }); // create a new group and add the option to it
        }
      } else {
        result.ungroupedOptions.push(option); // add the option to the ungrouped options
      }
    });

    return result;
  }, [options]);

  return (
    <Stack
      sx={{
        width: "230px",
        zIndex: 6,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "sm"
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: "600",
          lineHeight: "20px",
          background: "#fff",
          padding: "10px 10px 10px 15px"
        }}
      >
        {menuTitle}
      </Typography>
      <Stack sx={{ maxHeight: "400px", overflow: "auto" }}>
        {renderState.groups.map((group) => (
          <StyledAccordion
            key={group.key}
            onChange={() => handleChangeAccordion(group.key)}
            expanded={expanded === group.key}
          >
            <StyledAccordionSummary
              {...(expanded === group.key && { sx: { background: "#F6F8FC" } })}
              expandIcon={<ExpandMoreIcon sx={{ fill: "black" }} />}
            >
              {group.title}
            </StyledAccordionSummary>
            <Stack sx={{ height: "200px", overflow: "scroll" }}>
              {group.options.map((option) => {
                const isSelected = isColumnSelected(option);
                return (
                  <Stack
                    key={option.key}
                    sx={{
                      padding: "5px 10px",
                      cursor: "pointer",
                      background: isSelected ? "#DBE8F5" : "transparent",
                      ...(!isSelected && {
                        "&:hover": {
                          background: "#F6F8FC"
                        }
                      })
                    }}
                    onClick={() => handleChangeColumn(option)}
                  >
                    <Typography>{option.title}</Typography>
                  </Stack>
                );
              })}
            </Stack>
          </StyledAccordion>
        ))}
        {Boolean(renderState.ungroupedOptions.length) &&
          renderState.ungroupedOptions.map((option) => {
            const isSelected = isColumnSelected(option);

            return (
              <Stack
                key={option.key}
                sx={{
                  padding: "5px 10px",
                  cursor: "pointer",
                  background: isSelected ? "#DBE8F5" : "transparent",
                  ...(!isSelected && {
                    "&:hover": {
                      background: "#F6F8FC"
                    }
                  })
                }}
                onClick={() => handleChangeColumn(option)}
              >
                <Typography>{option.title}</Typography>
              </Stack>
            );
          })}
      </Stack>

      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap="10px"
        sx={{ padding: "10px", background: "#fff" }}
      >
        <Button size="sm" variant="outlined" sx={{ minWidth: "53px" }} onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="contained"
          sx={{ backgroundColor: "#071F35", minWidth: "46px" }}
          onClick={handleSubmit}
        >
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};
